/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Flag as FlagIcon,
  Chat as ChatIcon,
  Psychology as PsychologyIcon,
  Gavel as GavelIcon,
  VerifiedUser as VerifiedUserIcon,
  AutoAwesome as AutoAwesomeIcon,
  MenuBook as MenuBookIcon,
  Warning as WarningIcon,
  School as SchoolIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import logoImage from '../assets/images/gd-logo-text-plain-black.svg';

const AboutPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        {t('aboutPage.title')}
      </Typography>
      
      <Typography variant="h5" color="text.secondary" paragraph align="center" sx={{ mb: 6 }}>
        {t('aboutPage.subtitle')}
      </Typography>

      <Grid container spacing={4}>
        {/* Mission Statement */}
        <Grid item xs={12}>
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t('aboutPage.mission.title')}
            </Typography>
            <Typography paragraph>
              {t('aboutPage.mission.description')}
            </Typography>
            <Typography paragraph>
              {t('aboutPage.mission.goal')}
            </Typography>
          </Paper>
        </Grid>

        {/* Key Features */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            {t('aboutPage.keyFeatures.title')}
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <ChatIcon />
              </ListItemIcon>
              <ListItemText 
                primary={t('aboutPage.keyFeatures.structuredDebate')}
                secondary={t('aboutPage.keyFeatures.structuredDebateDescription')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <WarningIcon />
              </ListItemIcon>
              <ListItemText 
                primary={t('aboutPage.keyFeatures.jesterGauntlet')}
                secondary={t('aboutPage.keyFeatures.jesterGauntletDescription')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <GavelIcon />
              </ListItemIcon>
              <ListItemText 
                primary={t('aboutPage.keyFeatures.honorBasedScoring')}
                secondary={t('aboutPage.keyFeatures.honorBasedScoringDescription')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <VerifiedUserIcon />
              </ListItemIcon>
              <ListItemText 
                primary={t('aboutPage.keyFeatures.fairResolutions')}
                secondary={t('aboutPage.keyFeatures.fairResolutionsDescription')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <MenuBookIcon />
              </ListItemIcon>
              <ListItemText 
                primary={t('aboutPage.keyFeatures.debateSummaries')}
                secondary={t('aboutPage.keyFeatures.debateSummariesDescription')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AutoAwesomeIcon />
              </ListItemIcon>
              <ListItemText 
                primary={t('aboutPage.keyFeatures.cubeRepository')}
                secondary={t('aboutPage.keyFeatures.cubeRepositoryDescription')}
              />
            </ListItem>
          </List>
        </Grid>

        {/* Honorable Challenges */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            {t('aboutPage.honorableChallenges.title')}
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Typography paragraph>
              {t('aboutPage.honorableChallenges.description')}
            </Typography>
            <Typography paragraph>
              {t('aboutPage.honorableChallenges.howToChallenge')}
            </Typography>
            <Typography paragraph>
              {t('aboutPage.honorableChallenges.jesterGauntlet')}
            </Typography>
          </Paper>
        </Grid>

        {/* How It Works */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            {t('aboutPage.howItWorks.title')}
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Typography paragraph>
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {t('aboutPage.howItWorks.step1').split(':')[0]}:
              </Typography>
              {' ' + t('aboutPage.howItWorks.step1').split(':').slice(1).join(':')}
            </Typography>
            <Typography paragraph>
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {t('aboutPage.howItWorks.step2').split(':')[0]}:
              </Typography>
              {' ' + t('aboutPage.howItWorks.step2').split(':').slice(1).join(':')}
            </Typography>
            <Typography paragraph>
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {t('aboutPage.howItWorks.step3').split(':')[0]}:
              </Typography>
              {' ' + t('aboutPage.howItWorks.step3').split(':').slice(1).join(':')}
            </Typography>
            <Typography paragraph>
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {t('aboutPage.howItWorks.step4').split(':')[0]}:
              </Typography>
              {' ' + t('aboutPage.howItWorks.step4').split(':').slice(1).join(':')}
            </Typography>
            <Typography paragraph>
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {t('aboutPage.howItWorks.step5').split(':')[0]}:
              </Typography>
              {' ' + t('aboutPage.howItWorks.step5').split(':').slice(1).join(':')}
            </Typography>
          </Paper>
        </Grid>
        

        {/* CUBE System */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            {t('aboutPage.cubeSystem.title')}
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Typography paragraph>
              {t('aboutPage.cubeSystem.description')}
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              {t('aboutPage.cubeSystem.howCubeWorks')}
            </Typography>
            <Typography paragraph>
              {t('aboutPage.cubeSystem.cubeExtraction')}
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              {t('aboutPage.cubeSystem.challengeSystem')}
            </Typography>
            <Typography paragraph>
              {t('aboutPage.cubeSystem.challengeDescription')}
            </Typography>
            <Typography paragraph>
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {t('aboutPage.cubeSystem.challengeProcess').split(':')[0]}:
              </Typography>
              {' ' + t('aboutPage.cubeSystem.challengeProcess').split(':').slice(1).join(':')}
            </Typography>
            <Typography paragraph>
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {t('aboutPage.cubeSystem.cubePoints').split(':')[0]}:
              </Typography>
              {' ' + t('aboutPage.cubeSystem.cubePoints').split(':').slice(1).join(':')}
            </Typography>
            <Typography paragraph>
              {t('aboutPage.cubeSystem.continuousImprovement')}
            </Typography>
          </Paper>
        </Grid>

        {/* Excalibur Index */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            {t('aboutPage.excaliburIndex.title')}
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Typography paragraph>
              {t('aboutPage.excaliburIndex.description')}
            </Typography>
            <Typography paragraph>
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {t('aboutPage.excaliburIndex.calculation').split(':')[0]}:
              </Typography>
              {' ' + t('aboutPage.excaliburIndex.calculation').split(':').slice(1).join(':')}
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <AutoAwesomeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('aboutPage.excaliburIndex.qualityComponent')}
                  secondary={t('aboutPage.excaliburIndex.qualityComponentDescription')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <TrophyIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('aboutPage.excaliburIndex.performanceComponent')}
                  secondary={t('aboutPage.excaliburIndex.performanceComponentDescription')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PsychologyIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('aboutPage.excaliburIndex.goodFaithComponent')}
                  secondary={t('aboutPage.excaliburIndex.goodFaithComponentDescription')}
                />
              </ListItem>
            </List>
            <Typography paragraph>
              {t('aboutPage.excaliburIndex.scale')}
            </Typography>
            <Typography paragraph>
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {t('aboutPage.excaliburIndex.technicalImplementation').split(':')[0]}:
              </Typography>
              {' ' + t('aboutPage.excaliburIndex.technicalImplementation').split(':').slice(1).join(':')}
            </Typography>
            <Typography paragraph>
              {t('aboutPage.excaliburIndex.publicPrivateIndices')}
            </Typography>
          </Paper>
        </Grid>

        {/* Evolving System */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            {t('aboutPage.evolvingSystem.title')}
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Typography paragraph>
              {t('aboutPage.evolvingSystem.description')}
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <UpdateIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('aboutPage.evolvingSystem.communityDrivenImprovement')}
                  secondary={t('aboutPage.evolvingSystem.communityDrivenImprovementDescription')}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <SchoolIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('aboutPage.evolvingSystem.learningCurve')}
                  secondary={t('aboutPage.evolvingSystem.learningCurveDescription')}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <FlagIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('aboutPage.evolvingSystem.falsePositives')}
                  secondary={t('aboutPage.evolvingSystem.falsePositivesDescription')}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <AutoAwesomeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('aboutPage.evolvingSystem.cubeEvolution')}
                  secondary={t('aboutPage.evolvingSystem.cubeEvolutionDescription')}
                />
              </ListItem>
            </List>
            
            <Typography paragraph>
              {t('aboutPage.evolvingSystem.nature')}
            </Typography>
            
            <Typography paragraph>
              {t('aboutPage.evolvingSystem.reformulation')}
            </Typography>
          </Paper>
        </Grid>

        {/* Call to Action and Image Side by Side */}
        <Grid item xs={12} sx={{ mt: 6 }}>
          <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography variant="h5" gutterBottom>
                    {t('aboutPage.callToAction.title')}
                  </Typography>
                  <Typography paragraph>
                    {t('aboutPage.callToAction.description')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      component={RouterLink}
                      to="/debates/new"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ mt: 2 }}
                    >
                      {t('aboutPage.callToAction.startNewDebate')}
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/debates/public"
                      variant="contained"
                      color="success"
                      size="large"
                      sx={{ mt: 2 }}
                    >
                      {t('aboutPage.callToAction.checkPublicDebates')}
                    </Button>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <img 
                    src={logoImage} 
                    alt="Gauntlet Debates Logo" 
                    style={{ 
                      maxWidth: '100%',
                      height: 'auto',
                      maxHeight: '200px',
                      filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.3))',
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AboutPage; 